/**
 * @typedef {Object} LocationTeaserBanners
 *  @property {string} groupCity
 *  @property {string} groupRegio
 *  @property {string} groupOverall
 */

/**
 * @typedef {Object} SiteInfo
 * 	@property {string} clipsTag
 * 	@property {string} clipsOrder
 * 	@property {number} clipsPerPage
 * 	@property {number} clipsPage
 */

/**
 * @typedef {Object} siteConfig
 *  @property {string[]} savoUrls
 * 	@property {string} swiperConfig
 *  @property {boolean} showSolAds
 *  @property {LocationTeaserBanners} locationTeaserBanners
 */

/**
 * @typedef {Object} SearchConfig
 *  @property {string} overlayPos3Html
 * 	@property {string} overlayReminderHtml
 * 	@property {boolean} eigenwerbungBanners
 */

/**
 * @typedef {Object} userData
 *  @property {string} userId
 *  @property {boolean} isLoggedIn
 *  @property {boolean} isAgeVerified
 *  @property {boolean} hasBannerDisabled
 *  @property {string} userName
 *  @property {array} userAvatars
 */

/**
 * @var {WeakMap} elementData
 */

import { InfoPanel } from "@modules/info-panel/InfoPanel"
import { starsLikesLoginModal } from "@modules/stars/stars-likes-login-modal"
import { hasOwn } from "@modules/util/has-own"
import { getRequestUri } from "@modules/util/request-uri"
import { debounce } from "lodash-es"
import addEventListenersForStarsTermsOfUse from "../stars/stars-terms-of-use-check"
import { BaseEvent } from "../util/BaseEvent"
import { Cookie } from "../util/Cookie"
import { RAFQueue } from "../util/RafQueue"
import { isMobile } from "../util/device"
import { addBreakpointListener } from "../util/responsive"

const navigationType = Object.freeze({
    /**
     * Die Navigation im Browser fand zuletzt über eine Änderung an der URL
     * 	statt; entweder eine manuelle Eingabe, das Folgen eines Ankers, Setzen
     * 	über JavaScript, etc.
     */
    URL: 0,

    /**
     * Die Navigation im Browser war zuletzt das neu Laden der aktuellen Seite
     */
    Reload: 1,

    /**
     * Die Navigation im Browser fand zuletzt über die Zurück- oder Forwärts-
     * 	Button des Browsers selbst oder die entsprechenden JavaScript Funktionen
     * 	history.back() oder history.foward()
     */
    BackForward: 2,

    /**
     * Es fand keine Navigation statt, es wurde eine Seite, die über einen
     * 	Anker verlinkt wurde, nur mit der entsprechenden rel-Angabe vorgeladen
     */
    PreRender: 3,
})

export class MainClass {
    userCacheDataResetDoneCookie: Cookie
    feedbackCookie: Cookie
    filterDataCookie: Cookie
    orderedAdvertsCookie: Cookie
    orientationChanged: BaseEvent
    windowResized: BaseEvent
    responsiveChanged: BaseEvent
    modalBeforeOpened: BaseEvent
    modalBeforeClosed: BaseEvent
    nodesUpdated: BaseEvent
    currentPositionBeforeModalOpened: number
    orderedAdvertsConfig: string

    constructor() {
        const page = $("html, body")
        const win = $(window)
        const doc = $(document)

        document.documentElement.classList.add("starsSeparationActive")

        this.images = $("img")

        this.currentPositionBeforeModalOpened = 0

        // Global Events
        this.orientationChanged = new BaseEvent(this)

        this.windowResized = new BaseEvent(this)

        this.nodesUpdated = new BaseEvent(this)

        this.responsiveChanged = new BaseEvent(this)

        this.modalBeforeOpened = new BaseEvent(this)

        this.modalBeforeClosed = new BaseEvent(this)

        this.filterDataCookie = new Cookie("clearFilterData", {
            path: "/",
            domain: SiteInfo.cookieDomain,
        })

        this.userCacheDataResetDoneCookie = new Cookie("midnightUserCacheResetDone", {
            path: "/",
            domain: SiteInfo.cookieDomain,
        })

        this.feedbackCookie = new Cookie("hide-feedback", {
            path: "/",
            domain: SiteInfo.cookieDomain,
        })

        this.orderedAdvertsCookie = new Cookie("orderedAdvertsConfig", {
            path: "/",
            domain: SiteInfo.cookieDomain,
        })

        this.orderedAdvertsConfig = this.orderedAdvertsCookie.value || ""

        this.modalBeforeOpened.attach(this.prepareModal.bind(this))
        this.modalBeforeClosed.attach(this.closeModal.bind(this))

        // Global watchers
        win.on("orientationchange", this.notifyOrientationChange.bind(this))
        win.on(
            "updateGirls.bs.modal",
            function (this: MainClass, e: Event, data: any) {
                this.modalBeforeOpened.notify({
                    modal: {
                        target: e.target,
                        relatedTarget: data.relatedTarget,
                    },
                })
            }.bind(this),
        )

        page.on(
            "show.bs.modal",
            function (this: MainClass, e: Event) {
                this.modalBeforeOpened.notify({
                    modal: e,
                })
            }.bind(this),
        )

        page.on(
            "hidden.bs.modal",
            function (this: MainClass, e: Event) {
                this.modalBeforeClosed.notify({
                    modal: e,
                })
            }.bind(this),
        )

        // Next/previous watchers
        doc.on("click", ".more-in-ort-to-top", this.openRegioContainer.bind(this))
        doc.on("click", ".copy-to-clipboard", this.copyToClipboard.bind(this))

        const feedbackButton = $(".close-feedback-button")
        const submenuBackgroundInit = this.initSubMenuBackgroundGradient.bind(this)

        this.splitUrlParts()

        // Global Events
        this.orientationChanged = new BaseEvent(this)

        if (!isMobile()) {
            win.on(
                "resize",
                debounce(
                    this.windowResized.notify.bind(this.windowResized, {
                        debug: "windowResized",
                    }),
                    100,
                ),
            )

            this.windowResized.attach(submenuBackgroundInit)
            addBreakpointListener(this.responsiveChanged.notify.bind(this.responsiveChanged))

            $(".submenu-root").on("show.bs.collapse", "#ladies-nav-submenu", submenuBackgroundInit)

            $(".submenu-root").on("show.bs.collapse", "#ladies-nav-submenu", this.addOpeningClasses)
            $(".submenu-root").on("hide.bs.collapse", "#ladies-nav-submenu", this.addClosingClasses)
        }

        if (feedbackButton.length > 0) {
            feedbackButton.on("click", this.hideFeedbackButton.bind(this))
        }

        doc.on("click", this.closeSubMenuOnNonSubmenuClick.bind(this))

        doc.on("click", '[target="_blank"]', this.loseFocusAfterExternalLinkClick.bind(this))

        doc.on("touchstart", "[data-toggle]", (clickEvent: MouseEvent) => {
            // Wichtig für Mobile-Emulation, da ansonsten das click-Event
            //  nicht ausgeführt wird
            clickEvent.stopPropagation()
            clickEvent.preventDefault()
        })

        doc.on("click", "[data-toggle]", (clickEvent: MouseEvent) => {
            clickEvent.stopPropagation()
            clickEvent.preventDefault()
        })

        const closeMenuDropdowns = () => {
            $(".dropdown-open").removeClass("dropdown-open")
        }

        $(".menu-btn, .menu-btn > a").on("click", (clickEvent: MouseEvent) => {
            const listElement = $(clickEvent.currentTarget).closest("li")

            if (
                listElement.hasClass("dropdown-open") ||
                (clickEvent.target instanceof HTMLAnchorElement &&
                    $(clickEvent.target).closest(".drop-container").length > 0)
            ) {
                return
            }

            if ($("body").hasClass("is-touch-device")) {
                clickEvent.preventDefault()
                clickEvent.stopPropagation()

                listElement.addClass("dropdown-open")
                doc.on("click", closeMenuDropdowns)
            }
        })

        this.checkPresentImagesStatus($("img"))

        // Global watchers

        if (!isMobile()) {
            win.on("orientationchange", this.notifyOrientationChange.bind(this))
        }

        if (!isMobile()) {
            win.on("wheel", debounce(this.stopScrollAnimations, 250))
        }

        win.load(this.imageLoadOnReady.bind(this))

        doc.on("click", '.touch-hot-spot[href="#"]', this.ignoreEvent)

        doc.on("click", "[data-toggles]", this.toggleElement.bind(this))
        doc.on("click", ".print-button", (clickEvent: MouseEvent) => {
            clickEvent.stopPropagation()
            window.print()
        })

        doc.on("focus", "input.focus-selects-all", this.selectAllText.bind(this))

        doc.on("click", ".clear-filter", (_: MouseEvent) => {
            this.filterDataCookie.expires = Cookie.getExpireDate(3600 * 24)
            this.filterDataCookie.value = "1"
        })

        if ("index" === SiteInfo.controller) {
            // TODO stripe core?
            // Widgets.StripeCore.resetAllMoreStates()
        }

        // PC only
        if (SiteInfo.viewsFolderBase !== "mobile") {
            this.initScrollToTop()

            this.checkForPageNavigationType()
        }

        this.observeUserSearchDropdownInViewport(SiteInfo.savedFirstSearch)

        if (this.userCacheDataResetDoneCookie.value === "1") {
            this.displayUserdataReloadedMessageInInfoPanel()
        }

        addEventListenersForStarsTermsOfUse()
    }

    urlHost = ""

    /**
     * @type {String}
     * @public
     */
    urlPath = ""

    /**
     * @type {String[]}
     * @public
     */
    urlQuery: string[] = []

    /**
     * @type {String[]}
     * @public
     */
    urlQueryTmp: string[] = []

    /**
     * @type {jQuery}
     * @private
     */
    images: typeof $

    /**
     * @type {Number}
     * @private
     */
    subMenuCloseTimer = null

    jumpToScrollPoint(scrollPoint: any) {
        const otSc = scrollPoint.offset().top - 5

        $(scrollPoint.data("target")).animate(
            {
                scrollTop: otSc,
            },
            500,
        )
    }

    _getHashtagObject() {
        const hashtagObject: Record<string, string> = {}
        if (window.location.hash !== "") {
            for (const pairs of window.location.hash.substring(1).split("&")) {
                hashtagObject[pairs.split("=")[0]] = pairs.split("=")[1]
            }
        }
        return hashtagObject
    }

    _joinHashtagObject(hashtagObject: Record<string, string>) {
        const hashtagList = []
        for (const hashtag in hashtagObject) {
            if (!hasOwn(hashtagObject, hashtag)) continue
            const value = hashtagObject[hashtag]
            if (value !== null && value !== undefined && value !== "") {
                hashtagList.push(`${hashtag}=${value}`)
            } else {
                hashtagList.push(hashtag)
            }
        }
        return hashtagList.join("&")
    }
    _insertHashtagIntoUrl(hashtagStr: string) {
        // do not use window.location.hash to prevent site from scrolling
        let newUrl = window.location.href.split("#")[0]
        if (hashtagStr !== "") {
            newUrl = `${newUrl}#${hashtagStr}`
        }

        this.changeUrl({}, document.title, newUrl)
    }

    addHashtagToUrl(key: string, value = "") {
        const hashtags = this._getHashtagObject()

        hashtags[key] = value

        const hashtagStr = this._joinHashtagObject(hashtags)

        this._insertHashtagIntoUrl(hashtagStr)
    }

    showLoginModal() /** void */ {
        let loginModal = document.getElementById("modal-stars-likes-login")

        if (loginModal === null) {
            const loginModalHtml = Mustache.render(starsLikesLoginModal, {
                assetPath: SiteInfo.assetPath,
                referrer: getRequestUri(),
                loginUrl: SiteInfo.loginUrl,
                showBetaIcon: SiteInfo.showBetaIcons,
                betaIconLink: SiteInfo.betaIconsLink,
            })

            if (!loginModalHtml) {
                return
            }

            const loginModalFragment = new DOMParser().parseFromString(loginModalHtml, "text/html")
            const mainElement = document.body.querySelector("main")

            if (!mainElement) {
                return
            }

            if (loginModalFragment.firstChild) {
                mainElement.appendChild(loginModalFragment.firstChild)
            }
            loginModal = document.getElementById("modal-stars-likes-login")
        }

        $(loginModal).modal("show")
    }

    removeHashtagFromUrl(key: string) {
        const hashtags = this._getHashtagObject()

        delete hashtags[key]

        const hashtagStr = this._joinHashtagObject(hashtags)

        this._insertHashtagIntoUrl(hashtagStr)
    }

    checkHashtagInUrl(key: string, value = null) {
        const hashtags = this._getHashtagObject()

        if (value !== null) {
            return hashtags[key] === value
        }
        return key in hashtags
    }

    copyToClipboard(clickEvent: MouseEvent) {
        const clickElement = $(clickEvent.currentTarget)
        const copyFromElement = $(clickElement.data("target"))
        let copied = null

        if (!copyFromElement.length || !("execCommand" in document)) {
            return copied
        }

        // Selektiere den gesamten Inhalt des Ziel-Elements
        copyFromElement.select()

        try {
            // Versuche den Inhalt des Ziel-Elements zu kopieren
            copied = document.execCommand("copy")

            clickElement.trigger("copyToClipboard:success", {
                data: copied,
                element: copyFromElement,
            })

            if (undefined !== copied) {
                // TODO add for single advert views i guess
                // Share.displayCopySuccessTooltip(clickEvent)
            }
        } catch (ex) {}

        return copied
    }

    /**
     * Generische Methode zum Öffnen und Schließen von Containern
     *
     * @public
     * @param {Event} clickEvent
     */
    toggleElement(clickEvent: MouseEvent) {
        const clickElement = $(clickEvent.currentTarget)
        let parentSelector: string
        let parentElement: typeof $
        let toggleSelector: string
        let toggleElement: typeof $

        clickEvent.preventDefault()

        parentSelector = clickElement.data("parent")
        toggleSelector = clickElement.data("toggles")

        if (!/^[.#]/.test(parentSelector)) {
            parentSelector = `.${parentSelector}`
        }

        if (!/^[.#]/.test(toggleSelector)) {
            toggleSelector = `.${toggleSelector}`
        }

        parentElement = $(parentSelector)
        toggleElement = $(toggleSelector)

        if (parentElement.length > 0) {
            if (parentElement.hasClass("more-is-open")) {
                parentElement.removeClass("more-is-open")
            } else {
                parentElement.addClass("more-is-open")
            }
        }

        if (parentElement.length > 0) {
            toggleElement.slideToggle()
        }

        clickElement.blur()
    }

    /**
     * Aktiviert die Prüfung, ob das Benutzer-Suche Dropdown im sichtbaren
     *  Bereich des Browser liegt, um es in dem Fall dann automatisch
     *  aufzuklappen, damit Benutzer nach dem ersten Speichern sehen können,
     *  wo sich ihre Suchen befinden
     *
     * @public
     */
    observeUserSearchDropdownInViewport(savedFirstSearch: boolean) {
        if ("IntersectionObserver" in window) {
            const intersectionRoot = isMobile()
                ? document.querySelector("#page-saved-searches-container")
                : document.querySelector(".nav.navbar-nav.navbar-right")
            const intersections = new IntersectionObserver(this.displayUserSearchTop.bind(this), { threshold: 0.5 })

            if (intersectionRoot && siteConfig.userSearchCount > 0 && savedFirstSearch) {
                const el = intersectionRoot.querySelector(".bookmark-btn-menu")
                if (el) {
                    intersections.observe(el)
                }
            }
        }
    }

    displayUserdataReloadedMessageInInfoPanel() {
        this.userCacheDataResetDoneCookie.removeCookie()

        setTimeout(() => {
            InfoPanel.displayMessage(
                "Wir haben deine Anzeigen automatisch aktualisiert, für die neusten Angebote haben wir dich an den Anfang deiner Treffer-Liste weitergeleitet.",
                InfoPanel.Type.Success,
                10 * 1000, // Millisekunden
            )
        }, 300)
    }

    prepareModal(_: string, args: any) {
        if ($("body").hasClass("mobile-device") || $("body").hasClass("tablet-device")) {
            // Save current Position
            this.currentPositionBeforeModalOpened = $(document).scrollTop()

            $(args.modal.target).appendTo("body")
        }
        if (args.modal.target.id === "clustered-girls-modal" && args.modal.relatedTarget) {
            const girls = $(args.modal.relatedTarget).data("girls")
            const locationName = $(args.modal.relatedTarget).data("locationName")

            const html = Mustache.render(SearchConfig.templates.clusteredGirlsModalContentTemplate, {
                girls: girls,
            })

            $(args.modal.target).find(".modal-content-inner").html(html)
            $(args.modal.target).find(".modal-title").html(locationName)
        }
    }

    closeModal() {
        if ($("body").hasClass("mobile-device") || $("body").hasClass("tablet-device")) {
            // Scroll to current Position
            $("html, body").animate({ scrollTop: this.currentPositionBeforeModalOpened }, 0)
        }
    }

    splitUrlParts() {
        const protocol = location.protocol
        const host = location.host

        this.urlHost = `${protocol}//${host}`
        this.urlPath = location.pathname
        this.urlQuery = this.getQueryOptions(location.search)
    }

    changeUrl(stateObj: object, title: string, newUrl: string, pushNewUrl = "") {
        let url = newUrl
        // remove Domain
        if (this.urlHost) {
            url = url.replace(this.urlHost, "")
        }

        // login-ref-url, logout-ref-url
        const loginUrlsElements = $(".login-ref-url, .logout-ref-url")
        if (loginUrlsElements.length) {
            loginUrlsElements.map(() => {
                const oldUrl = $(this).attr("href")

                if (typeof oldUrl !== "undefined") {
                    const urlWithoutRootSlash = url.replace(/^\/+/g, "")
                    const newUrl = this.changeQueryOption(oldUrl, "ref", encodeURIComponent(urlWithoutRootSlash))

                    $(this).attr("href", newUrl)
                }
            })
        }

        // push new Url in history
        if (pushNewUrl) {
            history.pushState(stateObj, title, url)
        }
        // replace actual Url
        else {
            history.replaceState(stateObj, title, url)
        }
    }

    getQueryOptions(queryString: string) {
        const options: string[] = []
        let queryPart: string[]
        const query = queryString.replace("?", "").split("&")
        let queryIndex = 0
        const queryCount = query.length

        if (!siteConfig || !("optionsOrder" in siteConfig)) {
            return options
        }

        for (; queryIndex < queryCount; queryIndex++) {
            queryPart = query[queryIndex].split("=")
            options[siteConfig.optionsOrder[queryPart[0]]] = query[queryIndex]
        }

        return options
    }

    changeQueryOption(url: string, parameter: string, newValue: string) {
        //prefer to use l.search if you have a location/link object
        const urlparts = url.split("?")
        if (urlparts.length >= 2) {
            const prefix = `${encodeURIComponent(parameter)}=`
            const pars = urlparts[1].split(/[&;]/g)

            //reverse iteration as may be destructive
            for (let i = pars.length; i-- > 0; ) {
                //idiom for string.startsWith
                if (pars[i].lastIndexOf(prefix, 0) !== -1) {
                    pars[i] = prefix + newValue
                }
            }

            return urlparts[0] + (pars.length > 0 ? `?${pars.join("&")}` : "")
        }
        return url
    }

    private notifyOrientationChange() {
        this.orientationChanged.notify({
            debug: "orientationChanged",
            width: $(window).width(),
        })
    }

    openRegioContainer() {
        $(window).trigger("open.regio-selector", [])
    }

    /**
     * @private
     * @param {Event} clickEvent
     */
    hideFeedbackButton(clickEvent: MouseEvent) {
        const feedbackCloseButton = $(clickEvent.currentTarget)
        const feedbackButton = feedbackCloseButton.closest(".feedback, .feedback-button")

        clickEvent.stopPropagation()

        this.feedbackCookie.expires = Cookie.getExpireDate(3600 * 24 * 30) // 30 days
        this.feedbackCookie.value = "1"

        feedbackButton.remove()
    }

    private initSubMenuBackgroundGradient() {
        const subMenuGradientParent = $("nav .submenu")
        const subMenuContent = subMenuGradientParent.closest(".submenu-content")
        const subMenuGradient = subMenuContent.find(".gradient")
        const subMenuParentBaseGradient = subMenuGradientParent.closest(".gradient")

        if (!subMenuParentBaseGradient.length || !subMenuGradientParent.length || !subMenuGradient.length) {
            return
        }

        subMenuGradient.css({
            width: subMenuParentBaseGradient.width(),
            left: -Number.parseInt(subMenuContent.offset().left, 10),

            backgroundPosition: `top ${-subMenuParentBaseGradient.height()}px center`,

            visibility: "visible",
            opacity: 1,
        })
    }

    private addOpeningClasses(collapseEvent: MouseEvent) {
        $(collapseEvent.currentTarget).css("display", "block")

        // @ts-expect-error todo fix
        collapseEvent.currentTarget.classList.add("opening")
        // @ts-expect-error todo fix
        collapseEvent.currentTarget.classList.remove("closing")
    }

    /**
     * @private
     * @param {Event} collapseEvent
     */
    addClosingClasses(collapseEvent: MouseEvent) {
        // @ts-expect-error todo fix
        collapseEvent.currentTarget.classList.remove("opening")
        // @ts-expect-error todo fix
        collapseEvent.currentTarget.classList.add("closing")
    }

    /**
     * @private
     * @param {Cookie} firstUserSearchCookie
     * @param {IntersectionObserverEntry[]} entries
     * @param {IntersectionObserver} observer
     */
    displayUserSearchTop(entries: IntersectionObserverEntry[], observer: IntersectionObserver) {
        if (!entries) {
            return
        }

        const entryCount = entries.length
        let entryIndex = 0
        let userSearchBookmarkMenu = null
        let apiFirstSeenPrefix = ""

        for (; entryIndex < entryCount; entryIndex++) {
            if (
                !entries[entryIndex].isIntersecting ||
                !entries[entryIndex].target.classList.contains("bookmark-btn-menu")
            ) {
                continue
            }

            userSearchBookmarkMenu = entries[entryIndex].target
            break
        }

        if (!userSearchBookmarkMenu) {
            return
        }

        if (isMobile()) {
            apiFirstSeenPrefix = "/mobile"
        }

        // TODO: remove if-block when featureFlag is not needed anymore
        if (!SiteInfo.userSearchNewApi) {
            fetch(`${SiteInfo.yiiLadiesUrl}/api/user-suche${apiFirstSeenPrefix}/mark-first-seen`, {
                method: "POST",
                credentials: "include",
            }).then()
        }

        observer.unobserve(userSearchBookmarkMenu)

        const bookmarkMenuButton = userSearchBookmarkMenu.closest(".menu-btn")
        const userSearchDropdownTextElement = bookmarkMenuButton?.querySelector(".dropdown-text")

        if (!userSearchDropdownTextElement || !userSearchDropdownTextElement.querySelector(".first-search-info")) {
            return
        }

        userSearchDropdownTextElement.classList.remove("hidden", "hide")

        if (isMobile()) {
            $("#deine-suchen-dropdown").collapse("show")
        } else {
            bookmarkMenuButton?.classList.add("dropdown-open")

            const hidePopupCallback = () => {
                bookmarkMenuButton?.classList.remove("dropdown-open")
            }

            $(document).one("click", hidePopupCallback)
            window.setTimeout(hidePopupCallback, 6000)
        }
    }

    /**
     * @private
     */
    initScrollToTop() {
        const queue = new RAFQueue()
        const scrollBase = $(window)
        const scrollTopButton = $("#to-top-right")
        const tweenType = TWEEN.Easing.Cubic.Out
        let tweenStart: any
        let scrollTopTween: any

        scrollTopButton.click((clickEvent: MouseEvent) => {
            clickEvent.preventDefault()

            tweenStart = {
                scrollLeft: scrollBase.scrollLeft(),
                scrollTop: scrollBase.scrollTop(),
            }

            scrollTopTween = new TWEEN.Tween(tweenStart).to(
                {
                    scrollLeft: 0,
                    scrollTop: 0,
                },
                1600,
            )

            scrollTopTween.onUpdate(function () {
                // @ts-expect-error i dont know where this comes from. Tween? MainLcass? TODO fix
                scrollBase.scrollTop(this.scrollTop)
                // @ts-expect-error i dont know where this comes from. Tween? MainLcass? TODO fix
                scrollBase.scrollLeft(this.scrollLeft)
            })

            scrollTopTween.easing(tweenType)
            scrollTopTween.start()

            // @ts-expect-error typescript does not know that tween has a global update method
            queue.add(() => !TWEEN.update())
        })

        // Wenn manuell gescrollt oder getoucht wird, das Scrolling stoppen
        scrollBase.on("wheel touchstart", (breakEvent: MouseEvent & { deltaX: number; deltaY: number }) => {
            if (!scrollTopTween || ("wheel" === breakEvent.type && !breakEvent.deltaX && !breakEvent.deltaY)) {
                return
            }

            scrollTopTween.stop()
        })

        scrollBase.on("scroll", () => {
            if (scrollBase.scrollTop() > 600) {
                scrollTopButton.show()
            } else {
                scrollTopButton.hide()
            }
        })
    }

    /**
     * Verhindert das weitere, automatische Scrollen zum Seitenanfang,
     *    falls der User selbst anfängt zu scrollen (= kein Scroll-Blocking)
     *
     * @private
     */
    stopScrollAnimations() {
        const scrollElement = $("html, body")

        if (!scrollElement.find().length) {
            return
        }

        scrollElement.stop()
    }

    /**
     * Handling-Methode zum Setzen einer Klasse auf IMG-Elemente,
     *    bei denen das Laden der Source fehlgeschlagen ist
     *
     * @private
     * @param {Event} errorEvent
     */
    imageErrorHandler() {}

    /**
     * @private
     * @param {Event} successEvent
     */
    imageLoadHandler(successEvent: { currentTarget: HTMLElement }) {
        const successTriggerElement = $(successEvent.currentTarget)

        successTriggerElement.removeClass("not-found")
        successTriggerElement.closest(".image-loader").removeClass("load-image file-not-found")
    }

    /**
     * Läuft alle Bilder durch und setzt die Source für alle, die nicht beim
     *    Aufrufen der Seite direkt geladen werden sollten und die eigentliche
     *    Source-URL in einem entsprechenden Data-Attribut stehen haben
     *
     * @private
     */
    imageLoadOnReady() {
        const readyImages = $(".load-on-ready")
        let currentImageElement: typeof $
        let index: number

        this.checkPresentImagesStatus()

        for (index = 0; index < readyImages.length; ++index) {
            currentImageElement = $(readyImages.get(index))

            // Nur, wenn auch wirklich das Data-Attribut src gesetzt ist
            if (currentImageElement.data("src")) {
                currentImageElement.prop("src", currentImageElement.data("src"))

                // Entferne die Klasse und das Data-Attribut.
                //	Vermeidet, dass bereits durchlaufene Bilder nicht ein
                //	zweites mal die Source gesetzt bekommen.
                currentImageElement.removeAttr("data-src").removeClass("load-on-ready")
            }
        }
    }

    /**
     * Durchläuft alle Bilder, die bereits fertig geladen wurden und prüft,
     *    ob unter diesen Bildern welche ohne Höhe und Breite existieren.
     *    Sollten Höhe und Breite jeweils 0 sein, ist dies ein Indikator dafür,
     *    dass beim Laden etwas schief gelaufen ist und es wird der Error-
     *    Handler des Bildes ausgeführt.
     *
     * @private
     * @param {HTMLImageElement[]} images
     */
    checkPresentImagesStatus(images: any[] = []) {
        const useNatural = "naturalWidth" in document.createElement("img")
        let presentImages = images
        let count: number
        let index: number

        if (!presentImages) {
            presentImages = this.images.not(".not-found")
        }

        if (!presentImages.length) {
            return
        }

        for (index = 0, count = presentImages.length; index < count; ++index) {
            presentImages[index].setAttribute("data-image-id", `img-${index}`)
            this.checkImageStatus(presentImages[index], useNatural)
        }
    }

    /**
     * @see checkPresentImagesStatus
     *
     * @private
     * @param {HTMLImageElement} image
     * @param {Boolean} useNatural
     */
    checkImageStatus(image: HTMLImageElement, useNatural = false) {
        let widthAttribute: "width" | "naturalWidth" = "width"
        let heightAttribute: "height" | "naturalHeight" = "height"

        if (useNatural) {
            widthAttribute = "naturalWidth"
            heightAttribute = "naturalHeight"
        }

        if (!image.complete) {
            image.addEventListener("error", this.imageErrorHandler.bind(this))
            // @ts-expect-error yep todo fix later
            image.addEventListener("load", this.imageLoadHandler.bind(this))
            return
        }

        if (0 === image[widthAttribute] || 0 === image[heightAttribute]) {
            this.imageErrorHandler()
        } else {
            this.imageLoadHandler({ currentTarget: image })
        }
    }

    /**
     * Löst, wenn möglich, ein Event beim Navigieren im Browser aus:
     *  - navigation.load:page
     *
     *  Das Event gibt an die Callback-Methode als ersten Parameter den
     *  navigationType zurück (navigationType), als zweiten
     *  Parameter wird die aktuelle Navigation-Instanz übergeben.
     */
    checkForPageNavigationType() {
        let performanceData: PerformanceEntryList
        let navigation: PerformanceNavigation | PerformanceNavigationTiming | undefined
        const navigationTypes = {
            Navigate: 0,
            Reload: 0,
            BackForward: 0,
            PreRender: 0,
        } as Record<string, string | number>
        let dataCount: number
        let dataIndex = 0
        let eventType: string | number = ""

        if (!("performance" in window)) {
            return
        }

        if (!("PerformanceNavigationTiming" in window)) {
            navigation = performance.navigation

            navigationTypes.Navigate = navigation.TYPE_NAVIGATE
            navigationTypes.Reload = navigation.TYPE_RELOAD
            navigationTypes.BackForward = navigation.TYPE_BACK_FORWARD
            navigationTypes.PreRender = navigation.TYPE_RESERVED
        } else {
            performanceData = performance.getEntriesByType("navigation")
            dataCount = performanceData.length

            for (; dataIndex < dataCount; dataIndex++) {
                const entry = performanceData[dataIndex]

                if (entry instanceof PerformanceNavigationTiming) {
                    navigation = entry

                    navigationTypes.Navigate = "navigate"
                    navigationTypes.Reload = "reload"
                    navigationTypes.BackForward = "back_forward"
                    navigationTypes.PreRender = "prerender"

                    break
                }
            }
        }

        if (!navigation) {
            return
        }

        switch (navigation.type) {
            case navigationTypes.Navigate:
                eventType = navigationType.URL
                break

            case navigationTypes.Reload:
                eventType = navigationType.Reload
                break

            case navigationTypes.BackForward:
                eventType = navigationType.BackForward
                break

            case navigationTypes.PreRender:
                eventType = navigationType.PreRender
                break

            default:
                // Unbekannter Typ
                return
        }

        $(window).trigger("navigation.load:page", [eventType, navigation])
    }

    closeSubMenuOnNonSubmenuClick(clickEvent: MouseEvent) {
        const submenuContainer = $(clickEvent.target).closest("section.submenu")

        if (submenuContainer.length > 0) {
            return
        }

        $("section.submenu").closest(".collapse").collapse("hide")
    }

    loseFocusAfterExternalLinkClick(clickEvent: MouseEvent) {
        $(clickEvent.currentTarget).blur()
    }

    /**
     * @private
     * @param {Event} clickEvent
     */
    selectAllText(clickEvent: MouseEvent) {
        const textField = $(clickEvent.currentTarget)

        textField.select()
    }

    /**
     * @private
     * @param {Event} event
     */
    ignoreEvent(event: Event) {
        event.preventDefault()
    }
}

export const LadiesMain = new MainClass()
